export interface Point2D {
  x: number;
  y: number;
}

export interface Displacement2D {
  u: number;
  v: number;
}

export type VolumeDims = [height: number, width: number];

export type FrameData
  = | Float32Array
    | Uint16Array
    | Uint8Array
    | Int16Array
    | ArrayLike<number>;

export interface TracePointResult {
  n: number;
  frameIndex: number;
  point: Point2D;
  displacement: Displacement2D;
}

export interface TraceOptions {
  windowSize: number;
  pyramidLevels: number;
  maxIterationsPerLevel: number;
  convergenceEpsilon: number;
}

export interface GrayFrame {
  data: Float32Array;
  width: number;
  height: number;
}

const DEFAULT_OPTIONS: TraceOptions = {
  windowSize: 11,
  pyramidLevels: 3,
  maxIterationsPerLevel: 10,
  convergenceEpsilon: 0.01,
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function normalizeWindowSize(windowSize: number): number {
  const value = Math.max(3, Math.floor(windowSize));
  return value % 2 === 0 ? value + 1 : value;
}

function getFrameSlice(
  frames: ReadonlyArray<FrameData>,
  dims: VolumeDims,
  frameIndex: number,
): GrayFrame {
  const [height, width] = dims;
  const framesCount = frames.length;
  if (frameIndex < 0 || frameIndex >= framesCount) {
    throw new RangeError(`frameIndex out of range: ${frameIndex}`);
  }

  const frameSize = width * height;
  const view = frames[frameIndex];
  if (view.length !== frameSize) {
    throw new Error(
      `Invalid frame length for frame ${frameIndex}. Expected ${frameSize}, received ${view.length}.`,
    );
  }

  return {
    data:
      view instanceof Float32Array
        ? view
        : Float32Array.from(view as ArrayLike<number>),
    width,
    height,
  };
}

function getPixel(frame: GrayFrame, x: number, y: number): number {
  const ix = Math.round(x);
  const iy = Math.round(y);
  if (ix < 0 || iy < 0 || ix >= frame.width || iy >= frame.height) {
    return 0;
  }
  return frame.data[iy * frame.width + ix];
}

function bilinearSample(frame: GrayFrame, x: number, y: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = x0 + 1;
  const y1 = y0 + 1;
  const fx = x - x0;
  const fy = y - y0;

  const v00 = getPixel(frame, x0, y0);
  const v10 = getPixel(frame, x1, y0);
  const v01 = getPixel(frame, x0, y1);
  const v11 = getPixel(frame, x1, y1);

  return (
    v00 * (1 - fx) * (1 - fy)
    + v10 * fx * (1 - fy)
    + v01 * (1 - fx) * fy
    + v11 * fx * fy
  );
}

function downsample(frame: GrayFrame): GrayFrame {
  const dw = Math.max(1, Math.floor(frame.width / 2));
  const dh = Math.max(1, Math.floor(frame.height / 2));
  const out = new Float32Array(dw * dh);

  for (let y = 0; y < dh; y++) {
    for (let x = 0; x < dw; x++) {
      const sx = Math.min(frame.width - 1, x * 2);
      const sy = Math.min(frame.height - 1, y * 2);
      const sx1 = Math.min(frame.width - 1, sx + 1);
      const sy1 = Math.min(frame.height - 1, sy + 1);

      out[y * dw + x]
        = (frame.data[sy * frame.width + sx]
          + frame.data[sy * frame.width + sx1]
          + frame.data[sy1 * frame.width + sx]
          + frame.data[sy1 * frame.width + sx1])
        / 4;
    }
  }

  return { data: out, width: dw, height: dh };
}

function buildPyramid(frame: GrayFrame, levels: number): GrayFrame[] {
  const pyramid: GrayFrame[] = [frame];
  for (let level = 1; level < levels; level++) {
    const prev = pyramid[level - 1];
    if (prev.width <= 1 || prev.height <= 1) {
      break;
    }
    pyramid.push(downsample(prev));
  }
  return pyramid;
}

export function lucasKanadePointPyramidal(
  frameA: GrayFrame,
  frameB: GrayFrame,
  point: Point2D,
  options: TraceOptions,
): Displacement2D {
  const windowSize = normalizeWindowSize(options.windowSize);
  const halfWindow = Math.floor(windowSize / 2);

  const pyrA = buildPyramid(frameA, options.pyramidLevels);
  const pyrB = buildPyramid(frameB, options.pyramidLevels);
  const maxLevel = Math.min(pyrA.length, pyrB.length) - 1;

  let guessU = 0;
  let guessV = 0;

  for (let level = maxLevel; level >= 0; level--) {
    const scale = 2 ** level;
    const px = point.x / scale;
    const py = point.y / scale;
    const a = pyrA[level];
    const b = pyrB[level];

    let du = 0;
    let dv = 0;

    for (let iter = 0; iter < options.maxIterationsPerLevel; iter++) {
      let A11 = 0;
      let A12 = 0;
      let A22 = 0;
      let b1 = 0;
      let b2 = 0;

      for (let wy = -halfWindow; wy <= halfWindow; wy++) {
        for (let wx = -halfWindow; wx <= halfWindow; wx++) {
          const sx = px + wx;
          const sy = py + wy;

          const tx = sx + guessU / scale + du;
          const ty = sy + guessV / scale + dv;

          const Ix
            = (bilinearSample(a, sx + 1, sy) - bilinearSample(a, sx - 1, sy)) / 2;
          const Iy
            = (bilinearSample(a, sx, sy + 1) - bilinearSample(a, sx, sy - 1)) / 2;
          const It = bilinearSample(b, tx, ty) - bilinearSample(a, sx, sy);

          A11 += Ix * Ix;
          A12 += Ix * Iy;
          A22 += Iy * Iy;
          b1 += -Ix * It;
          b2 += -Iy * It;
        }
      }

      const det = A11 * A22 - A12 * A12;
      if (Math.abs(det) < 1e-9) {
        break;
      }

      const invDet = 1 / det;
      const stepU = (A22 * b1 - A12 * b2) * invDet;
      const stepV = (-A12 * b1 + A11 * b2) * invDet;

      du += stepU;
      dv += stepV;

      if (stepU * stepU + stepV * stepV < options.convergenceEpsilon) {
        break;
      }
    }

    guessU += du * scale;
    guessV += dv * scale;
  }

  return { u: guessU, v: guessV };
}

function validateInputs(
  startPoint: Point2D,
  startFrameIndex: number,
  framesData: ReadonlyArray<FrameData>,
  dims: VolumeDims,
): void {
  const [height, width] = dims;
  const framesCount = framesData.length;
  if (height <= 0 || width <= 0 || framesCount <= 0) {
    throw new Error(
      `Invalid dimensions [h, w, n]: [${height}, ${width}, ${framesCount}]`,
    );
  }

  if (
    !Number.isInteger(startFrameIndex)
    || startFrameIndex < 0
    || startFrameIndex >= framesCount
  ) {
    throw new RangeError(`startFrameIndex out of range: ${startFrameIndex}`);
  }

  const expectedSignalLength = height * width;
  for (let i = 0; i < framesCount; i++) {
    if (framesData[i].length !== expectedSignalLength) {
      throw new Error(
        `Frame ${i} length mismatch. Expected ${expectedSignalLength}, received ${framesData[i].length}.`,
      );
    }
  }

  if (!Number.isFinite(startPoint.x) || !Number.isFinite(startPoint.y)) {
    throw new TypeError('startPoint must contain finite x and y coordinates.');
  }
}

export function tracePointPyramidalLK(
  startPoint: Point2D,
  startFrameIndex: number,
  framesData: ReadonlyArray<FrameData>,
  dims: VolumeDims,
  partialOptions: Partial<TraceOptions> = {},
): TracePointResult[] {
  validateInputs(startPoint, startFrameIndex, framesData, dims);

  const [height, width] = dims;
  const framesCount = framesData.length;
  const options: TraceOptions = {
    ...DEFAULT_OPTIONS,
    ...partialOptions,
    windowSize: normalizeWindowSize(
      partialOptions.windowSize ?? DEFAULT_OPTIONS.windowSize,
    ),
    pyramidLevels: Math.max(
      1,
      Math.floor(partialOptions.pyramidLevels ?? DEFAULT_OPTIONS.pyramidLevels),
    ),
    maxIterationsPerLevel: Math.max(
      1,
      Math.floor(
        partialOptions.maxIterationsPerLevel
        ?? DEFAULT_OPTIONS.maxIterationsPerLevel,
      ),
    ),
    convergenceEpsilon: Math.max(
      1e-12,
      partialOptions.convergenceEpsilon ?? DEFAULT_OPTIONS.convergenceEpsilon,
    ),
  };

  const traced: TracePointResult[] = [];

  let currentPoint: Point2D = {
    x: clamp(startPoint.x, 0, width - 1),
    y: clamp(startPoint.y, 0, height - 1),
  };

  traced.push({
    n: 0,
    frameIndex: startFrameIndex,
    point: { ...currentPoint },
    displacement: { u: 0, v: 0 },
  });

  for (
    let frameIndex = startFrameIndex + 1;
    frameIndex < framesCount;
    frameIndex++
  ) {
    const prevFrame = getFrameSlice(framesData, dims, frameIndex - 1);
    const nextFrame = getFrameSlice(framesData, dims, frameIndex);

    const displacement = lucasKanadePointPyramidal(
      prevFrame,
      nextFrame,
      currentPoint,
      options,
    );

    currentPoint = {
      x: clamp(currentPoint.x + displacement.u, 0, width - 1),
      y: clamp(currentPoint.y + displacement.v, 0, height - 1),
    };

    traced.push({
      n: traced.length,
      frameIndex,
      point: { ...currentPoint },
      displacement,
    });
  }

  return traced;
}
